import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { ActionRecord, ActionStatus, InsightScope } from "@graftus/schema";
import { LogEvents, track } from "@/analytics/track";
import { useProfile } from "@/context/ProfileContext";
import { useIntelligenceAccess } from "@/hooks/use-intelligence-access";
import { DASHBOARD_TRPC_STALE_MS } from "@/lib/dashboard-trpc-stale";
import { useTRPC } from "@/trpc/client";

export interface DashboardActionsResult {
  /** All visible actions (proposed/accepted/done) with optimistic statuses applied. */
  actions: ActionRecord[];
  /** AI proposals awaiting a decision — the review queue. */
  proposed: ActionRecord[];
  /** Accepted to-dos. */
  accepted: ActionRecord[];
  /** Completed actions (newest decision first from the server sort). */
  done: ActionRecord[];
  loading: boolean;
  /** The actions list failed to load. */
  error: boolean;
  /** Re-fetches the failed actions list. */
  retry: () => void;
  accept: (actionId: string) => void;
  skip: (actionId: string) => void;
  complete: (actionId: string, outcomeNote?: string) => void;
}

/**
 * The dashboard to-do list: AI-proposed actions the user reviews (accept/skip)
 * and completes. Mirrors the optimistic dismiss pattern in
 * `useDashboardInsights` — status flips locally at once, rolls back on error,
 * and reconciles with the server via invalidation on settle.
 */
export function useActions(
  businessId: string | undefined,
  opts: { scope?: InsightScope; memberId?: string } = {},
): DashboardActionsResult {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { memberRole } = useProfile();
  const isManager = memberRole === "owner" || memberRole === "admin";
  const intelligenceEnabled = useIntelligenceAccess();
  const enabled = intelligenceEnabled && !!businessId;

  const query = useQuery(
    trpc.intelligence.actions.list.queryOptions(
      {
        businessId: businessId ?? "",
        ...(opts.scope ? { scope: opts.scope } : {}),
        ...(opts.memberId ? { memberId: opts.memberId } : {}),
      },
      { enabled, staleTime: DASHBOARD_TRPC_STALE_MS },
    ),
  );

  // Optimistic status overrides, reconciled by the server query. The complete
  // flow also carries the just-typed outcome note so it doesn't vanish while
  // the refetch is in flight.
  const [overrides, setOverrides] = useState<
    Map<string, { status: ActionStatus; outcomeNote?: string }>
  >(() => new Map());
  useEffect(() => {
    setOverrides(new Map());
  }, [businessId, opts.memberId]);

  const { mutate: acceptMutation } = useMutation(
    trpc.intelligence.actions.accept.mutationOptions(),
  );
  const { mutate: skipMutation } = useMutation(
    trpc.intelligence.actions.skip.mutationOptions(),
  );
  const { mutate: completeMutation } = useMutation(
    trpc.intelligence.actions.complete.mutationOptions(),
  );

  const setOverride = (
    actionId: string,
    status: ActionStatus,
    outcomeNote?: string,
  ) =>
    setOverrides((prev) =>
      new Map(prev).set(actionId, { status, outcomeNote }),
    );
  const clearOverride = (actionId: string) =>
    setOverrides((prev) => {
      const next = new Map(prev);
      next.delete(actionId);
      return next;
    });

  const callbacks = (actionId: string) => ({
    onError: () => clearOverride(actionId),
    onSettled: () => qc.invalidateQueries({ queryKey: [["intelligence"]] }),
  });

  const accept = (actionId: string) => {
    if (!businessId) return;
    setOverride(actionId, "accepted");
    // "Active managers per week" (GRA-217): accepting a platform-proposed
    // action is a meaningful manager action in its own right. When the
    // action traces back to an insight (`insightId`), this also doubles as
    // the "turned into action" kind of "share of accepted insights" - the
    // data model links the two here, not on the insight's own events.
    const record = ((query.data as ActionRecord[] | undefined) ?? []).find(
      (r) => r.id === actionId,
    );
    track({
      event: LogEvents.ActionAccepted.name,
      channel: LogEvents.ActionAccepted.channel,
      action_id: actionId,
      insight_id: record?.insightId ?? null,
      source: record?.source ?? null,
      scope: record?.scope ?? null,
      role: memberRole,
      is_manager: isManager,
    });
    acceptMutation({ businessId, actionId }, callbacks(actionId));
  };

  const skip = (actionId: string) => {
    if (!businessId) return;
    setOverride(actionId, "skipped");
    skipMutation({ businessId, actionId }, callbacks(actionId));
  };

  const complete = (actionId: string, outcomeNote?: string) => {
    if (!businessId) return;
    setOverride(actionId, "done", outcomeNote);
    completeMutation(
      { businessId, actionId, outcomeNote },
      callbacks(actionId),
    );
  };

  const actions = useMemo(() => {
    const records = (query.data as ActionRecord[] | undefined) ?? [];
    return records
      .map((r) => {
        const override = overrides.get(r.id);
        if (!override) return r;
        return {
          ...r,
          status: override.status,
          ...(override.outcomeNote !== undefined
            ? { outcomeNote: override.outcomeNote }
            : {}),
        };
      })
      .filter((r) => r.status !== "skipped" && r.status !== "expired");
  }, [query.data, overrides]);

  const proposed = useMemo(
    () => actions.filter((a) => a.status === "proposed"),
    [actions],
  );
  const accepted = useMemo(
    () => actions.filter((a) => a.status === "accepted"),
    [actions],
  );
  const done = useMemo(
    () => actions.filter((a) => a.status === "done"),
    [actions],
  );

  return {
    actions,
    proposed,
    accepted,
    done,
    loading: enabled && query.isPending,
    error: enabled && query.isError,
    retry: () => void query.refetch(),
    accept,
    skip,
    complete,
  };
}
